/* Reusable admin-mediation reply form for QI claims.
 *
 * POSTs `/admin/claims/{cid}/admin-reply` with {text, reason}; pushes a
 * `sender_kind=admin_mediation` event into claim.events and stamps
 * `admin_intervened=true`. Used in two places:
 *   • /admin/claims drawer (AdminClaims.jsx)
 *   • Order 360 → QI Claim tab (AdminOrder360Drawer.jsx)
 */
import { useState } from "react";
import api from "../lib/api";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Textarea } from "./ui/textarea";
import { toast } from "sonner";
import { Loader2, MessageCircle, Send } from "lucide-react";


export default function AdminClaimReply({ claim, onChange }) {
  const [text, setText] = useState("");
  const [reason, setReason] = useState("");
  const [busy, setBusy] = useState(false);

  if (!claim?.id) return null;

  const submit = async () => {
    if (!text.trim()) { toast.error("Message text required."); return; }
    if (reason.trim().length < 3) { toast.error("Reason is required (min 3 chars)."); return; }
    setBusy(true);
    try {
      const r = await api.post(`/admin/claims/${claim.id}/admin-reply`, {
        text: text.trim(),
        reason: reason.trim(),
      });
      toast.success("Mediation reply posted · audit-logged.");
      const newEvent = r.data?.event;
      if (onChange) {
        onChange(newEvent ? {
          ...claim,
          events: [...(claim.events || []), newEvent],
          admin_intervened: true,
        } : null);
      }
      setText(""); setReason("");
    } catch (e) {
      toast.error(e?.response?.data?.detail || e?.message || "Failed");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="rounded-xl border-2 border-indigo-200 bg-indigo-50/40 p-4" data-testid="admin-claim-reply">
      <div className="flex items-center gap-2 mb-2">
        <MessageCircle className="w-3.5 h-3.5 text-indigo-700"/>
        <h3 className="text-[12.5px] font-semibold text-indigo-900">Post admin mediation reply</h3>
      </div>
      <div className="text-[10.5px] text-indigo-800/80 mb-3">
        Adds a message tagged <span className="mono">sender_kind=admin_mediation</span> to the claim thread.
        Both buyer and seller will see it. Marks the claim as <span className="mono">admin_intervened</span>.
      </div>
      <Textarea
        rows={3}
        placeholder="Message to buyer + seller (visible to both)…"
        value={text}
        onChange={(e) => setText(e.target.value)}
        data-testid="admin-claim-reply-text"
        className="mb-2"
      />
      <Input
        placeholder="Reason (audit-logged · min 3 chars)"
        value={reason}
        onChange={(e) => setReason(e.target.value)}
        data-testid="admin-claim-reply-reason"
        className="mb-2"
      />
      <Button
        size="sm"
        onClick={submit}
        disabled={busy || !text.trim() || reason.trim().length < 3}
        data-testid="admin-claim-reply-submit"
        className="bg-indigo-700 hover:bg-indigo-800 text-white"
      >
        {busy ? <Loader2 className="w-3 h-3 mr-1 animate-spin"/> : <Send className="w-3 h-3 mr-1"/>}
        Post mediation reply
      </Button>
    </div>
  );
}
